import React from "react";
import { motion } from "framer-motion";
import { ChevronDown, Star, Ticket } from "lucide-react";
import { CONTACT, IMG, HERO_VIDEO } from "@/data/content";
import { useLead } from "@/components/site/LeadContext";
import Bubbles from "@/components/site/Bubbles";

const EASE = [0.16, 1, 0.3, 1];

function Rating({ dark = false }) {
  return (
    <div className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 ${dark ? "bg-brand-navy/5" : "bg-white/12 backdrop-blur-md"}`}>
      {[...Array(5)].map((_, i) => (
        <Star key={i} className="h-3.5 w-3.5 fill-sun text-sun" />
      ))}
      <span className={`ml-1 text-xs font-bold ${dark ? "text-brand-navy" : "text-white"}`}>4,7 · +500 no Google</span>
    </div>
  );
}

function Headline({ light }) {
  const base = light ? "text-white" : "text-brand-navy";
  return (
    <h1 aria-label="São 2 mergulhos pelo preço de 1" className="font-display font-black uppercase leading-[0.92] tracking-tight">
      <span className={`block text-[2.6rem] sm:text-6xl lg:text-7xl ${base}`}>São 2 mergulhos</span>
      <span className="block text-[2.6rem] text-sun sm:text-6xl lg:text-7xl">pelo preço de 1</span>
    </h1>
  );
}

function CtaBlock({ center }) {
  return (
    <div className={center ? "flex flex-col items-center" : ""}>
      <div className={center ? "flex flex-col items-center" : ""} data-testid="hero-voucher">
        {/* Big ticket highlight */}
        <div className={`flex ${center ? "flex-col items-center text-center" : "items-center"} gap-3`}>
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-brand-red/10 text-brand-red ring-2 ring-brand-red/25 sm:h-20 sm:w-20">
            <Ticket className="h-9 w-9 sm:h-11 sm:w-11" strokeWidth={2} />
          </div>
          <div className={center ? "mt-1" : ""}>
            <p className="font-display text-xl font-black uppercase leading-tight tracking-tight text-brand-navy sm:text-2xl">
              Ingresso válido por 1 ano
            </p>
            <p className="mt-0.5 text-sm font-bold uppercase tracking-wide text-brand-red sm:text-base">
              Use quando quiser, inclusive finais de semana, feriados ou até no verão que é a melhor época!
            </p>
          </div>
        </div>

        {/* Supporting lines (no icons) */}
        <div className={`mt-4 space-y-1.5 text-sm font-semibold text-ocean-mid ${center ? "text-center" : ""}`}>
          <p>Trabalhamos todos os dias do ano, exceto 24, 25 e 31/12 e 01/01</p>
        </div>
      </div>
    </div>
  );
}

export default function Hero() {
  const { openLead } = useLead();

  return (
    <section className="relative w-full overflow-hidden" data-testid="hero-section">
      {/* ================= MOBILE COVER (video on top + wave + headline) ================= */}
      <div className="md:hidden">
        {/* Video block on top */}
        <div className="relative w-full overflow-hidden bg-ocean-deep" style={{ aspectRatio: "4 / 5" }}>
          {HERO_VIDEO ? (
            <video
              className="absolute inset-0 h-full w-full object-cover"
              src={HERO_VIDEO}
              autoPlay
              muted
              loop
              playsInline
              data-testid="hero-video"
            />
          ) : (
            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg,#0F3A86,#05123A)" }} />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-ocean-deep/75 via-ocean-deep/10 to-ocean-deep/35" />

          {/* Logo + brand info overlay */}
          <div className="absolute inset-x-0 top-0 z-20 px-6 pt-10 text-center">
            <img
              src={IMG.logo}
              alt="Dive Master — Escola de Mergulho"
              className="mx-auto h-32 w-auto drop-shadow-[0_4px_20px_rgba(0,0,0,0.6)]"
            />
            <p className="mt-3 font-display text-base font-extrabold uppercase tracking-tight text-white drop-shadow-[0_2px_10px_rgba(0,0,0,0.6)]">
              Somos mestres em mergulho
            </p>
            <div className="mt-2 flex justify-center">
              <Rating />
            </div>
            <p className="mx-auto mt-2 max-w-xs text-xs font-semibold text-white/90 drop-shadow-[0_2px_10px_rgba(0,0,0,0.7)]">
              11 anos de experiência em Porto Belo / Bombinhas. Vem ser feliz!
            </p>
          </div>

          {/* Wave that cuts the video into the content */}
          <div className="absolute inset-x-0 bottom-0 z-10 leading-[0]">
            <svg viewBox="0 0 390 70" preserveAspectRatio="none" className="block h-[52px] w-full">
              <path d="M0,38 C90,72 150,8 210,20 C280,34 330,58 390,30 L390,70 L0,70 Z" fill="#1E56C0" opacity="0.9" />
              <path d="M0,50 C100,82 160,26 230,34 C300,42 340,64 390,44 L390,70 L0,70 Z" fill="#ffffff" />
            </svg>
          </div>
        </div>

        {/* Content on white */}
        <div className="relative bg-white px-6 pb-6 pt-8 text-center">
          <h1
            aria-label="Venha passear de barco, mergulhar com cilindro e ainda ganhe fotos subaquáticas"
            className="font-display text-2xl font-black uppercase leading-[1.15] tracking-tight text-brand-navy"
          >
            Venha <span className="text-sun">passear de barco</span>, mergulhar com{" "}
            <span className="text-sun">cilindro</span> e ainda ganhe{" "}
            <span className="text-sun">fotos subaquáticas</span>
          </h1>

          <p className="mb-2 mt-8 text-[0.72rem] font-bold uppercase tracking-[0.18em] text-ocean-mid">
            Aproveite essa oferta
          </p>
          <Headline />
          <p className="mx-auto mt-4 max-w-md font-accent text-xl leading-snug text-ocean-mid sm:text-2xl">
            Use 2x ou traga alguém especial com você.
          </p>
          <div className="mt-7">
            <CtaBlock center />
          </div>
        </div>
      </div>

      {/* ================= DESKTOP (video full-bleed background + overlay) ================= */}
      <div className="relative hidden min-h-[100svh] light-rays md:block">
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg,#0F3A86,#05123A)" }} />
        {HERO_VIDEO && (
          <video
            className="absolute inset-0 h-full w-full object-cover"
            src={HERO_VIDEO}
            autoPlay
            muted
            loop
            playsInline
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-ocean-deep/85 via-ocean-deep/55 to-ocean-deep/30" />
        <Bubbles count={22} />

        {/* top bar */}
        <div className="absolute inset-x-0 top-0 z-30 flex items-center justify-between px-8 py-5">
          <div className="flex items-center gap-3">
            <img src={IMG.logo} alt="Dive Master" className="h-16 w-auto drop-shadow-lg" />
            <span className="font-display text-xl font-extrabold uppercase tracking-tight text-white">{CONTACT.brand}</span>
          </div>
          <Rating />
        </div>

        <div className="relative z-20 flex min-h-[100svh] flex-col justify-center px-10 lg:px-16">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
            className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-ocean-glow"
          >
            Porto Belo · Bombinhas — Costa Esmeralda
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.3, ease: EASE }}
          >
            <Headline light />
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.6, ease: EASE }}
            className="mt-6 max-w-xl font-accent text-2xl leading-snug text-white/90 sm:text-3xl"
          >
            Use 2x ou traga alguém especial com você.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.8, ease: EASE }}
            className="mt-9 flex max-w-xl flex-col items-start gap-1"
          >
            <button
              onClick={() => openLead("Hero")}
              className="cta-sun botao-pulsante animate-pulse-glow inline-flex items-center justify-center rounded-full px-10 py-5 text-lg font-extrabold uppercase tracking-wide text-white shadow-2xl shadow-sun/30"
              data-testid="hero-cta-desktop"
            >
              Garantir ingresso na promoção
            </button>
            <div className="mt-5 flex items-start gap-4">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-brand-red/15 text-brand-red ring-2 ring-brand-red/25 backdrop-blur">
                <Ticket className="h-9 w-9" strokeWidth={2} />
              </div>
              <div>
                <p className="font-display text-xl font-black uppercase leading-tight tracking-tight text-white">
                  Ingresso válido por 1 ano
                </p>
                <p className="mt-0.5 text-sm font-bold uppercase tracking-wide text-sun">
                  Use quando quiser, inclusive finais de semana, feriados ou até no verão que é a melhor época!
                </p>
              </div>
            </div>
            <div className="mt-3 space-y-1 text-sm font-semibold text-white/85">
              <p>Trabalhamos todos os dias do ano, exceto 24, 25 e 31/12 e 01/01</p>
            </div>
          </motion.div>
        </div>

        <motion.div
          className="absolute inset-x-0 bottom-6 z-20 flex justify-center"
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
        >
          <ChevronDown className="h-6 w-6 text-white/70" />
        </motion.div>
      </div>
    </section>
  );
}
